<template>
    <div class="idp-progress-timeline">
        <div class="timeline-header">
            <h4><i class="bi bi-clock-history"></i> Report History</h4>
            <span class="report-count">{{ reports.length }} reports</span>
        </div>

        <div class="timeline-container" ref="timelineScroll">
            <div class="timeline-track">
                <div v-for="(report, index) in sortedReports" :key="report.id"
                    :class="['timeline-item', { active: isSelected(report), current: index === 0 }]"
                    @click="selectReport(report)">
                    <div class="timeline-marker">
                        <div class="marker-dot"></div>
                        <div class="marker-line" v-if="index < sortedReports.length - 1"></div>
                    </div>
                    <div class="timeline-content">
                        <div class="timeline-date">
                            {{ formatDate(report.created_date) }}
                        </div>
                        <div class="timeline-quarter">
                            {{ getQuarter(report.created_date) }}
                        </div>
                        <div class="timeline-preview">
                            <span class="preview-dot green"></span>
                            <span class="preview-dot yellow"></span>
                            <span class="preview-dot red"></span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="reports.length === 0" class="timeline-empty">
                <i class="bi bi-journal-plus"></i>
                <p>No reports yet</p>
            </div>
        </div>

        <!-- Mobile: Swipe indicator -->
        <div class="swipe-indicator" v-if="reports.length > 3">
            <i class="bi bi-chevron-left"></i>
            <span>Swipe to see more</span>
            <i class="bi bi-chevron-right"></i>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'IDPProgressTimeline',
    props: {
        reports: {
            type: Array,
            default: () => []
        },
        selectedReport: {
            type: Object,
            default: null
        }
    },
    computed: {
        sortedReports() {
            return [...this.reports].sort((a, b) =>
                new Date(b.created_date) - new Date(a.created_date)
            );
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('DD MMM YYYY');
        },
        getQuarter(date) {
            const quarter = moment(date).quarter();
            const year = moment(date).format('YYYY');
            return `Q${quarter} ${year}`;
        },
        isSelected(report) {
            return this.selectedReport && this.selectedReport.id === report.id;
        },
        selectReport(report) {
            this.$emit('select', report);
        }
    }
};
</script>

<style scoped lang="scss">
.idp-progress-timeline {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
}

.timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e2e8f0;

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: #1a202c;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
            color: #64748b;
        }
    }

    .report-count {
        font-size: 0.875rem;
        color: #64748b;
        background: #f1f5f9;
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
    }
}

.timeline-container {
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;

    &::-webkit-scrollbar {
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
    }
}

.timeline-track {
    display: flex;
    gap: 0;
    min-width: max-content;
    padding: 0.5rem 0;
}

.timeline-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 120px;
    padding: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;

    &:hover {
        background: #f8fafc;
    }

    &.active {
        background: #eff6ff;

        .marker-dot {
            background: #3b82f6;
            border-color: #3b82f6;
            transform: scale(1.2);
        }

        .timeline-date {
            color: #3b82f6;
            font-weight: 600;
        }
    }

    &.current {
        .marker-dot {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
        }
    }
}

.timeline-marker {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 0.75rem;
}

.marker-dot {
    width: 14px;
    height: 14px;
    background: #fff;
    border: 3px solid #cbd5e1;
    border-radius: 50%;
    z-index: 1;
    margin: 0 auto;
    transition: all 0.2s ease;
}

.marker-line {
    position: absolute;
    left: 50%;
    top: 50%;
    width: calc(100% + 0px);
    height: 2px;
    background: #e2e8f0;
    transform: translateX(7px);
}

.timeline-content {
    text-align: center;
}

.timeline-date {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #334155;
    margin-bottom: 0.25rem;
}

.timeline-quarter {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.5rem;
}

.timeline-preview {
    display: flex;
    justify-content: center;
    gap: 0.25rem;
}

.preview-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &.green {
        background: #22c55e;
    }

    &.yellow {
        background: #eab308;
    }

    &.red {
        background: #ef4444;
    }
}

.timeline-empty {
    text-align: center;
    padding: 2rem;
    color: #94a3b8;

    i {
        font-size: 2rem;
        margin-bottom: 0.5rem;
        display: block;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
    }
}

.swipe-indicator {
    display: none;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    font-size: 0.75rem;
    color: #94a3b8;

    i {
        font-size: 0.625rem;
    }
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .idp-progress-timeline {
        padding: 1rem;
    }

    .timeline-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;

        .report-count {
            font-size: 0.75rem;
        }
    }

    .timeline-item {
        min-width: 100px;
    }

    .swipe-indicator {
        display: flex;
    }
}

@media (max-width: 480px) {
    .timeline-item {
        min-width: 90px;
        padding: 0.375rem;
    }

    .timeline-date {
        font-size: 0.75rem;
    }

    .timeline-quarter {
        font-size: 0.6875rem;
    }

    .marker-dot {
        width: 12px;
        height: 12px;
        border-width: 2px;
    }
}
</style>
